/**
 * „Skojarzenia miejsc” (owner 2026-10-30 22:23: „PFK - sopot mamuszki 14”): a phrase in the name of a new event gives
 * its place. Pure functions, used by the server (the key that keeps a phrase once) and by the web app (the matching
 * while the name is typed, the phrase offered by „Zapamiętać?”).
 */

/** Words of a text: lower case, no diacritics („ł” as „l”), every run of other characters one space. */
export function phraseKey(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/ł/g, 'l')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

const words = (text: string): string[] => {
  const key = phraseKey(text);
  return key === '' ? [] : key.split(' ');
};

/** Whether `phrase` (its words in a row) is in `name` as whole words. */
function contains(name: readonly string[], phrase: readonly string[]): boolean {
  if (phrase.length === 0 || phrase.length > name.length) return false;
  for (let i = 0; i + phrase.length <= name.length; i++) {
    if (phrase.every((w, j) => name[i + j] === w)) return true;
  }
  return false;
}

/**
 * The rule whose phrase is in the name — whole words, without case or diacritics; of several, the longest phrase
 * (then the one with more words, then the first). Null when none.
 */
export function matchPlaceRule<R extends { phrase: string }>(name: string, rules: readonly R[]): R | null {
  const inName = words(name);
  if (inName.length === 0) return null;
  let best: { rule: R; length: number; count: number } | null = null;
  for (const rule of rules) {
    const phrase = words(rule.phrase);
    if (!contains(inName, phrase)) continue;
    const length = phrase.join(' ').length;
    if (!best || length > best.length || (length === best.length && phrase.length > best.count)) best = { rule, length, count: phrase.length };
  }
  return best?.rule ?? null;
}

/**
 * The phrase „Zapamiętać?” offers for a name: its first acronym (a word of two or more capitals, e.g. „PFK”), else its
 * first word; as written (case and diacritics kept), without punctuation around it. '' when the name has no word.
 */
export function suggestPhrase(name: string): string {
  const tokens = name.split(/[^\p{L}\p{N}]+/u).filter((t) => t !== '');
  const acronym = tokens.find((t) => /^\p{Lu}{2,}\p{N}*$/u.test(t));
  return acronym ?? tokens[0] ?? '';
}
